if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.navPrev = this.querySelector('.product-media-modal__nav--prev');
        this.navNext = this.querySelector('.product-media-modal__nav--next');
        this.content = this.querySelector('[role="document"]');
        this.dots = Array.from(this.querySelectorAll('.product-media-modal__dot'));
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this._tap = { t: 0, x: 0, y: 0 };
        this._drag = { on: false, x: 0, y: 0, tx: 0, ty: 0, pointerId: null, target: null };

        // Dawn's base ModalDialog closes media modals on pointerup (mouse) unless the target is inside
        // deferred-media/product-model. Our modal includes images + custom controls, so stop pointerup
        // bubbling from inside the dialog. Backdrop clicks (target === this) still close the modal.
        if (this.dialog) {
          this.dialog.addEventListener('pointerup', (e) => e.stopPropagation());
        }

        if (this.navPrev) this.navPrev.addEventListener('click', () => this.go(-1));
        if (this.navNext) this.navNext.addEventListener('click', () => this.go(1));

        if (this.dots.length) {
          this.dots.forEach((dot) => {
            dot.addEventListener('click', () => {
              const id = dot.getAttribute('data-media-id');
              if (!id) return;
              this.dataset.activeMediaId = id;
              this.showActiveMedia();
            });
          });
        }

        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.go(-1);
          if (e.key === 'ArrowRight') this.go(1);
        });

        // Basic swipe support (Flipkart-like) for touch devices
        let startX = null;
        this.addEventListener(
          'touchstart',
          (e) => {
            if (!e.touches || !e.touches[0]) return;
            if (this.classList.contains('is-zoomed')) return;
            startX = e.touches[0].clientX;
          },
          { passive: true }
        );
        this.addEventListener(
          'touchend',
          (e) => {
            if (startX == null) return;
            if (this.classList.contains('is-zoomed')) return;
            const endX = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : null;
            if (endX == null) return;
            const dx = endX - startX;
            startX = null;
            if (Math.abs(dx) < 40) return;
            this.go(dx < 0 ? 1 : -1);
          },
          { passive: true }
        );
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        // Track active media id for nav-driven changes.
        this.dataset.activeMediaId = opener && opener.getAttribute ? opener.getAttribute('data-media-id') : '';
        this.showActiveMedia();
      }

      getActiveMediaId() {
        return (
          (this.dataset && this.dataset.activeMediaId) ||
          (this.openedBy && this.openedBy.getAttribute && this.openedBy.getAttribute('data-media-id')) ||
          ''
        );
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('[role="document"] [data-media-id]'));
      }

      setNavState() {
        const items = this.getMediaItems();
        if (!items.length) return;
        const activeId = this.getActiveMediaId();
        const idx = Math.max(
          0,
          items.findIndex((el) => el.getAttribute('data-media-id') === activeId)
        );
        if (this.navPrev) this.navPrev.toggleAttribute('disabled', idx <= 0);
        if (this.navNext) this.navNext.toggleAttribute('disabled', idx >= items.length - 1);
      }

      setDotsState() {
        if (!this.dots || !this.dots.length) return;
        const activeId = this.getActiveMediaId();
        this.dots.forEach((dot) => {
          const isActive = dot.getAttribute('data-media-id') === activeId;
          dot.classList.toggle('is-active', isActive);
        });
      }

      go(delta) {
        const items = this.getMediaItems();
        if (!items.length) return;
        const activeId = this.getActiveMediaId();
        let idx = items.findIndex((el) => el.getAttribute('data-media-id') === activeId);
        if (idx < 0) idx = 0;
        const next = idx + delta;
        if (next < 0 || next >= items.length) return;
        const nextId = items[next].getAttribute('data-media-id');
        this.dataset.activeMediaId = nextId;
        this.showActiveMedia();
      }

      warmUpMedia(items, index) {
        const targets = [items[index], items[index - 1], items[index + 1]].filter(Boolean);

        targets.forEach((item, offsetIndex) => {
          const img =
            item.tagName === 'IMG' && item.matches('img[data-media-id]')
              ? item
              : item.querySelector && item.querySelector('img');

          if (!img) return;

          if (offsetIndex === 0) {
            img.loading = 'eager';
            img.fetchPriority = 'high';
          } else {
            img.fetchPriority = 'low';
          }

          if (img.dataset && img.dataset.modalWarm === 'true') return;
          if (img.dataset) img.dataset.modalWarm = 'true';

          const preload = new Image();
          preload.decoding = 'async';
          preload.src = img.currentSrc || img.src;
        });
      }

      clearZoom() {
        this.classList.remove('is-zoomed');
        this.querySelectorAll('.product-media-modal__content > *.is-zoomed').forEach((el) => {
          el.classList.remove('is-zoomed');
          el.style.removeProperty('--zoom-tx');
          el.style.removeProperty('--zoom-ty');
        });
        this._drag.on = false;
        this._drag.pointerId = null;
        this._drag.target = null;
      }

      toggleZoom(activeMediaEl, origin) {
        if (!activeMediaEl) return;
        const isOn = !activeMediaEl.classList.contains('is-zoomed');
        this.clearZoom();
        if (!isOn) return;

        activeMediaEl.classList.add('is-zoomed');
        this.classList.add('is-zoomed');

        const img =
          activeMediaEl.tagName === 'IMG' && activeMediaEl.matches('img[data-media-id]')
            ? activeMediaEl
            : activeMediaEl.querySelector && activeMediaEl.querySelector('img[data-media-id]');
        if (img && origin) {
          img.style.transformOrigin = `${origin.x}% ${origin.y}%`;
        } else if (img) {
          img.style.transformOrigin = '50% 50%';
        }
      }

      bindZoomHandlers(activeMediaEl) {
        if (!activeMediaEl) return;

        // In this theme, image media inside the modal is rendered as a bare <img data-media-id="...">.
        // Other media types are wrapped elements. Support both shapes.
        const img =
          activeMediaEl.tagName === 'IMG' && activeMediaEl.matches('img[data-media-id]')
            ? activeMediaEl
            : activeMediaEl.querySelector && activeMediaEl.querySelector('img[data-media-id]');
        if (!img) return;

        const boundHost = img; // bind once per image node
        if (boundHost.dataset.zoomBound === 'true') return;
        boundHost.dataset.zoomBound = 'true';

        const getOriginPct = (clientX, clientY) => {
          const r = img.getBoundingClientRect();
          const x = ((clientX - r.left) / Math.max(1, r.width)) * 100;
          const y = ((clientY - r.top) / Math.max(1, r.height)) * 100;
          return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
        };

        // Desktop: double click
        img.addEventListener('dblclick', (e) => {
          e.preventDefault();
          const o = getOriginPct(e.clientX, e.clientY);
          this.toggleZoom(activeMediaEl, o);
        });

        // Mobile: double tap
        img.addEventListener(
          'touchend',
          (e) => {
            if (!e.changedTouches || !e.changedTouches[0]) return;
            const t = Date.now();
            const x = e.changedTouches[0].clientX;
            const y = e.changedTouches[0].clientY;
            const dt = t - this._tap.t;
            const dx = Math.abs(x - this._tap.x);
            const dy = Math.abs(y - this._tap.y);
            this._tap = { t, x, y };
            if (dt < 320 && dx < 22 && dy < 22) {
              const o = getOriginPct(x, y);
              this.toggleZoom(activeMediaEl, o);
            }
          },
          { passive: true }
        );

        // Drag to pan when zoomed (simple, no bounds).
        img.addEventListener('pointerdown', (e) => {
          if (!activeMediaEl.classList.contains('is-zoomed')) return;
          this._drag.on = true;
          this._drag.pointerId = e.pointerId;
          this._drag.x = e.clientX;
          this._drag.y = e.clientY;
          this._drag.target = activeMediaEl;
          const tx = parseFloat(activeMediaEl.style.getPropertyValue('--zoom-tx')) || 0;
          const ty = parseFloat(activeMediaEl.style.getPropertyValue('--zoom-ty')) || 0;
          this._drag.tx = tx;
          this._drag.ty = ty;
          img.setPointerCapture(e.pointerId);
        });

        img.addEventListener('pointermove', (e) => {
          if (!this._drag.on || this._drag.pointerId !== e.pointerId) return;
          if (!this._drag.target || !this._drag.target.classList.contains('is-zoomed')) return;
          const dx = e.clientX - this._drag.x;
          const dy = e.clientY - this._drag.y;
          const ntx = this._drag.tx + dx;
          const nty = this._drag.ty + dy;
          this._drag.target.style.setProperty('--zoom-tx', `${ntx}px`);
          this._drag.target.style.setProperty('--zoom-ty', `${nty}px`);
        });

        const endDrag = (e) => {
          if (this._drag.pointerId !== e.pointerId) return;
          this._drag.on = false;
          this._drag.pointerId = null;
          this._drag.target = null;
        };

        img.addEventListener('pointerup', endDrag);
        img.addEventListener('pointercancel', endDrag);
      }

      showActiveMedia() {
        const activeId = this.getActiveMediaId();
        if (!activeId) return;

        this.clearZoom();

        this.querySelectorAll(`[data-media-id]:not([data-media-id="${activeId}"])`).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${activeId}"]`);
        const items = this.getMediaItems();
        const activeIndex = Math.max(
          0,
          items.findIndex((el) => el.getAttribute('data-media-id') === activeId)
        );
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();
        this.bindZoomHandlers(activeMedia);
        this.warmUpMedia(items, activeIndex);

        const container = this.querySelector('[role="document"]');
        if (container) {
          const w = activeMedia.offsetWidth || activeMedia.clientWidth || 0;
          container.scrollLeft = Math.max(0, (w - container.clientWidth) / 2);
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        this.setNavState();
        this.setDotsState();
      }
    }
  );
}
